const HTML_TAG_PATTERN = /<\/?[a-z][^>]*>/i

/** Bullets without `locationId` apply to every location. */
export function filterActivityTypeBulletsForLocation<
  T extends { locationId?: string | null }
>(bullets: T[], locationId?: string | null): T[] {
  if (!locationId) {
    return bullets.filter(bullet => !bullet.locationId)
  }
  return bullets.filter(
    bullet => !bullet.locationId || bullet.locationId === locationId
  )
}

/**
 * The description bullet is the bullet flagged `isDescription` whose
 * `locationId` matches the given location, falling back to the generic
 * (no `locationId`) bullet flagged `isDescription`. When no bullet is
 * flagged, the first applicable bullet is used so the page always has a
 * description. The description bullet is placed first in the returned
 * array, followed by the remaining applicable bullets (matching location
 * or generic) in existing list order.
 *
 * When a location-specific description is used, the generic (default)
 * description bullet is dropped from the remaining bullets so it does not
 * leak into the page as a feature/accordion item — the location's own
 * description takes its place.
 */
export function activityTypeBulletsForProductHero<
  T extends { locationId?: string | null; isDescription?: boolean }
>(bullets: T[], locationId?: string | null): T[] {
  const applicableBullets = filterActivityTypeBulletsForLocation(
    bullets,
    locationId
  )
  if (applicableBullets.length === 0) return []

  const locationDescriptionBullet = applicableBullets.find(
    bullet =>
      locationId && bullet.locationId === locationId && bullet.isDescription
  )
  const defaultDescriptionBullet = applicableBullets.find(
    bullet => !bullet.locationId && bullet.isDescription
  )
  const descriptionBullet =
    locationDescriptionBullet ??
    defaultDescriptionBullet ??
    applicableBullets[0]

  if (!descriptionBullet) return applicableBullets

  // Bullets to omit from the trailing (feature/accordion) list: always the
  // chosen description bullet, plus the default description bullet whenever a
  // location-specific description replaced it.
  const excludedBullets = new Set<T>([descriptionBullet])
  if (locationDescriptionBullet && defaultDescriptionBullet) {
    excludedBullets.add(defaultDescriptionBullet)
  }

  return [
    descriptionBullet,
    ...applicableBullets.filter(bullet => !excludedBullets.has(bullet))
  ]
}

export const getActivityTypeDescription = (
  value?: string | null
): string | undefined => {
  // Full rich text, rendered like the other tabs' content.
  const trimmedValue = value?.trim()
  return trimmedValue || undefined
}

export const getActivityTypeFeatures = (
  bullets: Array<{ value?: string | null }>
): string[] =>
  bullets
    .slice(1)
    .map(bullet => bullet.value?.trim() ?? "")
    .filter(
      (value): value is string =>
        value.length > 0 && !HTML_TAG_PATTERN.test(value)
    )
